import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaEllipsisV, FaFlag, FaBan, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { REPORT_CATEGORIES } from '@/constants/reportCategories';
import { useAppDispatch } from '@/redux/Store';
import { removeThread } from '@/redux/slices/messagesSlice';
import './UserActionsMenu.scss';

interface Props {
  targetUserId: string;
  targetName: string;
  threadId?: string;
  onThreadDeleted?: () => void;
  /** Render a custom trigger instead of the default ellipsis button */
  trigger?: React.ReactNode;
  /**
   * Gate the open transition — return `false` to abort. Used by callers
   * to route anon users to signup instead of surfacing the report/block
   * flow they can't complete anyway.
   */
  onBeforeOpen?: () => boolean;
}

type View = 'menu' | 'report-category' | 'report-reason' | 'report-details' | 'block-confirm' | 'delete-confirm';

const UserActionsMenu: React.FC<Props> = ({
  targetUserId,
  targetName,
  threadId,
  onThreadDeleted,
  trigger,
  onBeforeOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('menu');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  // Close on outside click. The dropdown lives in a portal outside
  // `containerRef`, so check both the trigger container AND the
  // dropdown node before dismissing.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setView('menu');
    setSelectedCategory('');
    setSelectedReason('');
    setDetails('');
    setAnchorRect(null);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      handleClose();
    } else {
      if (onBeforeOpen && !onBeforeOpen()) return;
      setView('menu');
      setAnchorRect(containerRef.current?.getBoundingClientRect() ?? null);
      setIsOpen(true);
    }
  };

  const handleBlock = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/users/${targetUserId}/block`);
      toast.success(`${targetName} has been blocked`);
      handleClose();
    } catch {
      toast.error('Failed to block user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteThread = async () => {
    if (!threadId) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/messages/threads/${threadId}`);
      dispatch(removeThread(threadId));
      toast.success('Conversation removed from your inbox');
      handleClose();
      onThreadDeleted?.();
    } catch {
      toast.error('Failed to delete conversation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReportSubmit = async () => {
    if (!selectedCategory) return;
    setIsSubmitting(true);
    try {
      await api.post(`/users/${targetUserId}/report`, {
        category: selectedCategory,
        reason: selectedReason || undefined,
        details: details.trim() || undefined,
      });
      toast.success('Report submitted. Thank you for keeping Klyx safe.');
      handleClose();
    } catch {
      toast.error('Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeCategoryData = REPORT_CATEGORIES.find(c => c.value === selectedCategory);

  const renderContent = () => {
    switch (view) {
      case 'menu':
        return (
          <ul className="uam-list">
            <li className="uam-item uam-item--danger" onClick={() => setView('report-category')}>
              <FaFlag className="uam-icon" /> Report
            </li>
            <li className="uam-item uam-item--danger" onClick={() => setView('block-confirm')}>
              <FaBan className="uam-icon" /> Block
            </li>
            {threadId && (
              <li className="uam-item uam-item--danger" onClick={() => setView('delete-confirm')}>
                <FaTrash className="uam-icon" /> Delete Conversation
              </li>
            )}
          </ul>
        );

      case 'report-category':
        return (
          <div className="uam-form">
            <div className="uam-form-header">
              <button className="uam-back" onClick={() => setView('menu')}>← Back</button>
              <span>Report {targetName}</span>
            </div>
            <p className="uam-label">What's the issue?</p>
            <ul className="uam-list">
              {REPORT_CATEGORIES.map(cat => (
                <li
                  key={cat.value}
                  className="uam-item"
                  onClick={() => {
                    setSelectedCategory(cat.value);
                    setSelectedReason('');
                    setView(cat.subcategories.length > 0 ? 'report-reason' : 'report-details');
                  }}
                >
                  {cat.label}
                </li>
              ))}
            </ul>
          </div>
        );

      case 'report-reason':
        return (
          <div className="uam-form">
            <div className="uam-form-header">
              <button className="uam-back" onClick={() => setView('report-category')}>← Back</button>
              <span>{activeCategoryData?.label}</span>
            </div>
            <p className="uam-label">Select a reason</p>
            <ul className="uam-list">
              {activeCategoryData?.subcategories.map(sub => (
                <li
                  key={sub.value}
                  className="uam-item"
                  onClick={() => {
                    setSelectedReason(sub.value);
                    setView('report-details');
                  }}
                >
                  {sub.label}
                </li>
              ))}
            </ul>
          </div>
        );

      case 'report-details':
        return (
          <div className="uam-form">
            <div className="uam-form-header">
              <button className="uam-back" onClick={() => setView(activeCategoryData?.subcategories.length ? 'report-reason' : 'report-category')}>← Back</button>
              <span>Additional details</span>
            </div>
            <p className="uam-label">Anything else to add? <span className="uam-optional">(optional)</span></p>
            <textarea
              className="uam-textarea"
              placeholder="Describe what happened..."
              value={details}
              onChange={e => setDetails(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <button
              className="uam-submit"
              onClick={handleReportSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        );

      case 'block-confirm':
        return (
          <div className="uam-form">
            <div className="uam-form-header">
              <button className="uam-back" onClick={() => setView('menu')}>← Back</button>
              <span>Block User</span>
            </div>
            <p className="uam-confirm-text">
              Block <strong>{targetName}</strong>? They won't be able to message you or view your profile.
            </p>
            <div className="uam-actions">
              <button className="uam-cancel" onClick={() => setView('menu')}>Cancel</button>
              <button className="uam-submit uam-submit--danger" onClick={handleBlock} disabled={isSubmitting}>
                {isSubmitting ? 'Blocking...' : 'Block'}
              </button>
            </div>
          </div>
        );

      case 'delete-confirm':
        return (
          <div className="uam-form">
            <div className="uam-form-header">
              <button className="uam-back" onClick={() => setView('menu')}>← Back</button>
              <span>Delete Conversation</span>
            </div>
            <p className="uam-confirm-text">
              Remove this conversation with <strong>{targetName}</strong> from your inbox?
              It stays visible to them, and the thread will reappear for you
              if they send a new message.
            </p>
            <div className="uam-actions">
              <button className="uam-cancel" onClick={() => setView('menu')}>Cancel</button>
              <button className="uam-submit uam-submit--danger" onClick={handleDeleteThread} disabled={isSubmitting}>
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="uam" ref={containerRef}>
      <div className="uam-trigger" onClick={handleToggle}>
        {trigger ?? (
          <button className="uam-default-btn" aria-label="More options">
            <FaEllipsisV />
          </button>
        )}
      </div>
      {isOpen && anchorRect && ReactDOM.createPortal(
        // Portalled to `document.body` so ancestor `overflow: hidden`
        // (e.g. `.profile-hero`) can't clip the dropdown. Positioned
        // in viewport coords via the trigger's bounding rect at open
        // time; right-anchored so it flushes against the trigger's
        // right edge.
        <div
          ref={dropdownRef}
          className="uam-dropdown uam-dropdown--portalled"
          style={{
            position: 'fixed',
            top: anchorRect.bottom + 6,
            right: Math.max(8, window.innerWidth - anchorRect.right),
          }}
        >
          {renderContent()}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default UserActionsMenu;
